import { useState } from "react";
import { AthleteSelect } from "@/components/AthleteSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Users2 } from "lucide-react";

export function ParticipantsTab({ session, athletes, groups, nameOf, rankOf = () => 9999, onSave, onDistribute, locked, onImportFromTraining, trainingTitle }) {
  const [athleteId, setAthleteId] = useState("");
  const [ext, setExt] = useState("");
  const [nPools, setNPools] = useState(2);
  const parts = session.participants || [];
  const used = parts.map((p) => p.athlete_id).filter(Boolean);

  const addAthlete = (id) => { if (!id) return; onSave([...parts, { key: id, athlete_id: id }]); setAthleteId(""); };
  const addExt = () => { if (!ext.trim()) return; onSave([...parts, { key: `ext:${crypto.randomUUID()}`, external_name: ext.trim() }]); setExt(""); };
  const remove = (key) => onSave(parts.filter((p) => p.key !== key));

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="participants-tab">
      <div className="space-y-4">
        <div className="bg-white border border-zinc-200 rounded-md p-4 space-y-3">
          <div className="font-heading font-semibold text-sm flex items-center gap-2"><Users2 className="w-4 h-4" />Aggiungi partecipanti</div>
          {onImportFromTraining && (
            <Button data-testid="part-import-training" variant="outline" className="w-full h-12 justify-start" onClick={onImportFromTraining}>
              <Users2 className="w-4 h-4 mr-2" />Importa dalla seduta «{trainingTitle}» (presenti o roster)
            </Button>
          )}
          <div><Label>Atleta CSP</Label><AthleteSelect value={athleteId} onChange={addAthlete} athletes={athletes} groups={groups} excludeIds={used} placeholder="Cerca e aggiungi atleta…" testid="part-athlete" triggerClassName="h-12" /></div>
          <div>
            <Label>Avversario esterno</Label>
            <div className="flex gap-2">
              <Input data-testid="part-ext-name" className="h-12" value={ext} onChange={(e) => setExt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addExt()} placeholder="Cognome Nome (società)" />
              <Button data-testid="part-ext-add" variant="outline" className="h-12" onClick={addExt}>Aggiungi</Button>
            </div>
          </div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-md p-4 space-y-3">
          <div className="font-heading font-semibold text-sm">Componi i gironi</div>
          <p className="text-xs text-zinc-500">Distribuzione a serpentina per ranking interno (dagli assalti di simulazione già disputati; senza storico = 9999, in coda). Dopo la generazione i gironi restano liberamente modificabili.</p>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Numero gironi</Label>
            <Input type="number" min={1} max={8} data-testid="pools-count" className="h-10 w-20" value={nPools} onChange={(e) => setNPools(Math.max(1, parseInt(e.target.value || 1)))} />
            <Button data-testid="pools-generate" disabled={parts.length < 3} onClick={() => onDistribute(nPools)} className="bg-black hover:bg-zinc-800 h-10">{locked ? "Rigenera gironi" : "Genera gironi"}</Button>
          </div>
          {locked && <div className="text-xs text-amber-700">Rigenerando, gli assalti già disputati tra coppie che restano nello stesso girone vengono conservati.</div>}
        </div>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md p-4">
        <div className="font-heading font-semibold text-sm mb-2">Iscritti · {parts.length}</div>
        {parts.length === 0 ? <div className="text-sm text-zinc-500" data-testid="participants-empty">Nessun partecipante.</div> : (
          <div className="divide-y divide-zinc-100" data-testid="participants-list">
            {parts.map((p, i) => (
              <div key={p.key} data-testid={`participant-${p.key}`} className="flex items-center gap-3 py-2 text-sm">
                <span className="w-6 text-right font-mono text-xs text-zinc-400">{i + 1}</span>
                <span className="flex-1 truncate">{nameOf(p.key)}{!p.athlete_id && <span className="ml-2 text-[10px] uppercase text-zinc-400">esterno</span>}</span>
                <span className="font-mono text-[10px] text-zinc-400" data-testid={`participant-rank-${p.key}`} title="Ranking interno simulazioni (9999 = nessuno storico)">{rankOf(p.key) === 9999 ? "R —" : `R${rankOf(p.key)}`}</span>
                <button data-testid={`participant-remove-${p.key}`} onClick={() => remove(p.key)} className="p-1 text-zinc-400 hover:text-red-600"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
